import { reactive } from "vue";
import api from "./apiEndpoints.js";
import http from "./http.js";
import messages from "./messages.js";
import store from "./vuex.js";
import eventBus from "./eventBus.js";
import { generalOptions } from "./config.js";

function createAuthService() {
    const state = reactive({
        user: null,
        token: null,
        remember: 0,
        stylist_id: null,
        stylist_alias: null,
    });

    const auth = {
        api,
        http,
        message: messages,
        store,
        eventBus,
        generalOptions,

        private_routes: ["/profile"],

        private_admin_routes: [
            "/app",
            "/app/team",
            "/app/services",
            "/app/schedule",
            "/app/free-time",
            "/app/calendar",
            "/app/stylists",
            "/app/marketing",
            "/app/profile",
            "/app/payments",
            "/app/clients",
            "/app/subscription-payments",
            "/app/referral",
            "/app/contact-us",
            "/app/block-out",
            "/app/payments/credit-cards",
            "/app/payments/transactions",
            "/app/work-settings",
            "/app/work-settings/service-hours",
            "/app/work-settings/business-license",
            "/app/work-settings/notifications",
            "/app/marketing/special-offers",
            "/app/marketing/push-notifications",
            "/app/marketing/text-messages",
            "/app/marketing/social-links",
            "/app/marketing/booking-widget",
            "/app/payments/settings",
            "/app/payments/refunds",
            "/app/payments/statements",
            "/app/policy/deposits",
            "/app/policy/cancellation-noshow",
            "/app/policy/reschedule",
            "/app/policy/per-client-restrictions",
            "/app/policy/age-limit",
            "/app/policy/policy-summary",
            "/app/subscribe",
            "/app/payouts",
            "/app/reports/appts",
            "/app/reports/products",
        ],

        // ----------------------- Methods -----------------------
        login(data, adminLogin) {
            const request_data = {
                grant_type: "password",
                client_id: this.api.CLIENT_ID,
                client_secret: this.api.CLIENT_SECRET,
                username: data.email,
                password: data.password,
                scope: "",
            };

            return this.http.post(this.api.actions.login, request_data).then(
                (response) => {
                    if (response.status === 200) {
                        if (!response.data?.access_token) {
                            this.message.errorMessage(this.message.GLOBAL_MESSAGES.LOGIN_ERROR);
                        } else {
                            this.onLogin(response.data, data.remember);
                            this.setDeviceInfo();
                            this.getProfile(adminLogin);
                        }
                    }
                },
                () => {
                    this.message.errorMessage(this.message.GLOBAL_MESSAGES.LOGIN_ERROR);
                },
            );
        },

        resetPassword(data) {
            const request_data = {
                email: data.email,
                app_id: data.app_id,
                web: true,
            };

            return this.http.post(this.api.actions.reset_password, request_data).then(
                (response) => {
                    if (response.status === 200 && !response.data?.error) {
                        this.eventBus.emit("reset_password_email_sent");
                    }
                },
                () => {
                    this.message.errorMessage(this.message.GLOBAL_MESSAGES.RESET_PASS_ERROR);
                },
            );
        },

        setPassword(data) {
            const request_data = {
                token: data.token,
                password: data.password,
                password_confirm: data.confirmPassword,
            };

            return this.http.post(this.api.actions.set_password, request_data).then(
                (response) => {
                    if (response.status === 200 && !response.data?.error) {
                        this.eventBus.emit("change_password_success");
                    }
                },
                () => {
                    this.message.errorMessage(this.message.GLOBAL_MESSAGES.RESET_PASS_ERROR);
                },
            );
        },

        onLogin(token, remember) {
            state.token = token;
            state.token.expire_time = Date.now() / 1000 + token.expires_in;

            localStorage.setItem("token", JSON.stringify(token));
            localStorage.setItem("remember", remember);

            this.eventBus.emit("user_logged_in");
        },

        checkClientRole() {
            if (state.user && state.user.role === "client") return false;

            return this.http.get(this.api.actions.get_stylist_subaccount_email).then(
                (response) => {
                    if (response.status === 200 && response.data?.email) {
                        this.login({
                            email: response.data.email,
                            password: state.user.email,
                        });
                    }
                },
                () => false,
            );
        },

        register(data) {
            this.http.post(this.api.actions.register, data).then(
                (response) => {
                    if (response.data.error) {
                        this.message.errorMessage(response.data.message);
                    }
                    if (response.status === 200 && response.data?.user) {
                        this.login(
                            {
                                email: data.email,
                                password: data.password,
                                remember: 1,
                            },
                            true,
                        ).then(() => {
                          const phone = this.$store?.state?.selectedStylist?.user?.phone;

                          if (phone) {
                              this.http.post(this.api.actions.send_invite, {
                                  phone,
                              });
                          }
                        });
                    }
                },
                () => false,
            );
        },

        refreshToken(next) {
            const remember = parseInt(localStorage.getItem("remember")) || 0;
            if (!remember) {
                this.logout(false);
                next();
                return;
            }

            const request_data = {
                grant_type: "refresh_token",
                client_id: this.api.CLIENT_ID,
                client_secret: this.api.CLIENT_SECRET,
                refresh_token: state.token.refresh_token,
                scope: "",
            };

            this.http.post(this.api.actions.login, request_data).then(
                (response) => {
                    if (response.status === 200 && response.data?.access_token) {
                        state.token = response.data;
                        state.token.expire_time = Date.now() / 1000 + response.data.expires_in;

                        localStorage.setItem("token", JSON.stringify(state.token));
                        this.eventBus.emit("token_refreshed");

                        next();
                    } else {
                        this.message.errorMessage(this.REQUEST_ERROR);
                    }
                },
                () => {
                    this.message.errorMessage(this.REQUEST_ERROR);
                },
            );
        },

        getProfile(adminLogin) {
            this.http.get(this.api.actions.get_profile).then(
                (response) => {
                    if (response.status === 200 && response.data?.user) {
                        state.user = response.data.user;

                        localStorage.setItem("user", JSON.stringify(state.user));

                        if (!adminLogin) {
                            this.checkClientRole();
                        }

                        this.eventBus.emit("user_profile_loaded");
                    } else {
                        this.message.errorMessage(this.REQUEST_ERROR);
                    }
                },
                () => {
                    this.message.errorMessage(this.REQUEST_ERROR);
                },
            );
        },

        setDeviceInfo() {
            this.http.post(this.api.actions.set_device_info, {
                app_id: generalOptions.app_id,
            });
        },

        async logout(revoke_token) {
            if (revoke_token) {
                try {
                    await this.http.get(this.api.actions.logout);
                } catch (error) {
                    console.warn("Logout API failed:", error.response?.status);
                    // Ignore 401 because token may already be invalid
                }
            }

            localStorage.removeItem("token");
            localStorage.removeItem("user");
            localStorage.removeItem("remember");
            localStorage.removeItem("calendarView");
            localStorage.removeItem("calendarDate");
            localStorage.removeItem("ShowAllAppts");

            state.user = null;
            state.token = null;
            state.remember = 0;

            this.eventBus.emit("user_logged_out");
            this.store.commit("resetState");
        },

        // reactive state
        state,
    };

    // Restore saved data safely
    try {
        state.user = JSON.parse(localStorage.getItem("user"));
        state.token = JSON.parse(localStorage.getItem("token"));
        state.remember = parseInt(localStorage.getItem("remember")) || 0;
        state.stylist_id = localStorage.getItem("stylist_id");
        state.stylist_alias = localStorage.getItem("stylist_alias");
    } catch (e) {
        auth.logout?.(); // use optional chaining to avoid errors
    }

    return auth;
}

// Export a ready-to-use singleton
const auth = createAuthService();
export default auth;
